import { useMemo } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import I18n from 'I18n';
import { useProductTypeSearchParam } from './useProductTypeSearchParams';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useAppliedFilters } from '../filters/hooks/useAppliedFilters';
import { useFilterConfig } from '../filters/hooks/useFilterConfig';
import { useSearchResultsQuery } from '../search-results/hooks/useSearchResultsQuery';
export function useSearchResultPageMetadata() {
  var _data$result$total;
  const productType = useProductTypeSearchParam();
  const searchParams = useSearchParams();
  const appliedFilters = useAppliedFilters();
  const filterConfig = useFilterConfig();
  const {
    data
  } = useSearchResultsQuery();
  const searchTerm = searchParams.get('search');
  const count = (_data$result$total = data === null || data === void 0 ? void 0 : data.result.total) !== null && _data$result$total !== void 0 ? _data$result$total : 0;
  const formattedCount = I18n.formatNumber(count);
  return useMemo(() => {
    const hasSearchTerm = Boolean(searchTerm);

    // Default case (no filters, no search)
    if (!hasSearchTerm && !productType && appliedFilters.length === 0) {
      return {
        title: unescapedText('marketplaceStorefront.documentHead.searchResults.default.title', {
          count: formattedCount
        }),
        heading: unescapedText('marketplaceStorefront.searchResults.header.allProducts'),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.default.metaDescription')
      };
    }

    // App product type handling
    if (productType === 'APP') {
      var _categoryFilter$value, _filterConfig$Categor;
      // Get category filter for apps
      const categoryFilter = appliedFilters.find(f => f.field === 'CATEGORY');
      const singleCategory = (categoryFilter === null || categoryFilter === void 0 || (_categoryFilter$value = categoryFilter.values) === null || _categoryFilter$value === void 0 ? void 0 : _categoryFilter$value.length) === 1 ? categoryFilter.values[0] : null;

      // App without category or multiple categories
      if (!singleCategory) {
        return {
          title: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.title', {
            count: formattedCount
          }),
          heading: unescapedText('marketplaceStorefront.searchResults.header.allApps'),
          metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.metaDescription')
        };
      }

      // App with single category
      const categoryOption = (_filterConfig$Categor = filterConfig.Category) === null || _filterConfig$Categor === void 0 || (_filterConfig$Categor = _filterConfig$Categor.options) === null || _filterConfig$Categor === void 0 ? void 0 : _filterConfig$Categor.find(opt => opt.value === singleCategory);
      const categoryName = (categoryOption === null || categoryOption === void 0 ? void 0 : categoryOption.text) || singleCategory;
      return {
        title: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.categoryTitle', {
          count: formattedCount,
          category: categoryName
        }),
        heading: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.categoryHeading', {
          category: categoryName
        }),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.categoryMetaDescription', {
          category: categoryName
        })
      };
    }

    // Agent product type handling
    if (productType === 'AGENT') {
      var _teamFilter$values, _filterConfig$AgentTe;
      // Get team filter for agents
      const teamFilter = appliedFilters.find(f => f.field === 'AGENT_TEAM');
      const singleTeam = (teamFilter === null || teamFilter === void 0 || (_teamFilter$values = teamFilter.values) === null || _teamFilter$values === void 0 ? void 0 : _teamFilter$values.length) === 1 ? teamFilter.values[0] : null;

      // Agent without team or multiple teams
      if (!singleTeam) {
        return {
          title: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.title', {
            count: formattedCount
          }),
          heading: unescapedText('marketplaceStorefront.searchResults.header.allAgents'),
          metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.metaDescription')
        };
      }

      // Agent with single team
      const teamOption = (_filterConfig$AgentTe = filterConfig.AgentTeam) === null || _filterConfig$AgentTe === void 0 || (_filterConfig$AgentTe = _filterConfig$AgentTe.options) === null || _filterConfig$AgentTe === void 0 ? void 0 : _filterConfig$AgentTe.find(opt => opt.value === singleTeam);
      const teamName = (teamOption === null || teamOption === void 0 ? void 0 : teamOption.text) || singleTeam;
      return {
        title: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.teamTitle', {
          count: formattedCount,
          team: teamName
        }),
        heading: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.teamHeading', {
          team: teamName
        }),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.teamMetaDescription', {
          team: teamName
        })
      };
    }

    // Fallback to default with search
    return {
      title: unescapedText('marketplaceStorefront.documentHead.searchResults.defaultWithSearch.title', {
        count: formattedCount
      }),
      heading: unescapedText('marketplaceStorefront.headers.searchResults'),
      metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.defaultWithSearch.metaDescription')
    };
  }, [productType, searchTerm, appliedFilters, filterConfig, formattedCount]);
}